<script setup lang="ts">
/* eslint-env vue/setup-compiler-macros */
import { ref } from 'vue';
import Select from 'primevue/select';
import Textarea from 'primevue/textarea';
import { useToast } from 'primevue/usetoast';
import AppModal from '@/components/ui/AppModal.vue';
import FormField from '@/components/ui/FormField.vue';
import { rejectPayment } from '@/services/paymentService';
import { ApiError } from '@/models/errors/ApiError';

interface Props {
    visible: boolean
    paymentId: number | null
}

const props = defineProps<Props>();

const emit = defineEmits<{
    'update:visible': [value: boolean]
    rejected: [paymentId: number]
}>();

const REJECT_REASONS = [
    'Account Closed',
    'Authorization Revoked',
    'Duplicate Transaction',
    'Fraud Suspected',
    'Incorrect Amount',
    'Payment Disputed',
    'Wrong Debtor',
    'Other',
];

const reason = ref<string | null>(null);
const notes = ref('');
const saving = ref(false);
const formError = ref('');

const toast = useToast();

async function handleReject() {
    if (!props.paymentId || !reason.value) return;
    saving.value = true;
    formError.value = '';
    try {
        await rejectPayment(props.paymentId, reason.value, notes.value || undefined);
        toast.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Payment rejected successfully.',
            life: 3000,
        });
        emit('rejected', props.paymentId);
        emit('update:visible', false);
        resetForm();
    } catch (error) {
        const apiError = ApiError.from(error);
        if (apiError) {
            const data = apiError.data as { message?: string } | undefined;
            formError.value = typeof data?.message === 'string'
                ? data.message
                : 'An error occurred. Please try again.';
        } else {
            formError.value = 'An unexpected error occurred. Please try again.';
        }
    } finally {
        saving.value = false;
    }
}

function resetForm() {
    reason.value = null;
    notes.value = '';
    formError.value = '';
}

function handleCancel() {
    resetForm();
}
</script>

<template>
    <AppModal
        :visible="visible"
        title="Reject Payment"
        action-label="Reject Payment"
        :action-disabled="!reason || saving"
        @update:visible="emit('update:visible', $event)"
        @cancel="handleCancel"
        @action="handleReject"
    >
        <FormField
            id="reject-reason"
            label="Reject Reason"
            class="reject-modal__reason-field"
            required
        >
            <Select
                id="reject-reason"
                v-model="reason"
                class="reject-modal__reason-select"
                :options="REJECT_REASONS"
                placeholder=" "
            />
        </FormField>

        <FormField id="additional-notes" label="Additional Notes">
            <Textarea
                id="additional-notes"
                draggable="false"
                v-model="notes"
                rows="6"
                style="resize: none" 
                placeholder=" "
            />
        </FormField>

        <p v-if="formError" class="reject-modal__error">{{ formError }}</p>
    </AppModal>
</template>

<style lang="scss" scoped>
:deep(.reject-modal__reason-field .p-select) {
    box-sizing: border-box;
    padding-right: 25px !important;
}

:deep(.reject-modal__reason-field .p-select .p-select-label) {
    padding: 0 25px !important;
    height: 100%;
}

:deep(.reject-modal__reason-select:not(.p-inputwrapper-focus):not(.p-inputwrapper-filled) + label) {
    top: calc(50% - 2px) !important;
    transform: translateY(-50%) !important;
}

.reject-modal__error {
    margin: 8px 0 0;
    font-size: 13px;
    color: $error-red;
}
</style>


